import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { read, utils } from 'xlsx';

const getApiUrl = () => {
  if (window.location.hostname !== 'localhost') return window.location.origin;
  return window.location.port === '3001' ? 'http://localhost:3000' : 'http://localhost:3000';
};

const API_URL = getApiUrl();

function TemplateSelector({ onSelect, onClose, initialRecipients = [], theme = 'light', isBroadcasting = false, broadcastProgress = null, onCancelBroadcast }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [availableContacts, setAvailableContacts] = useState(initialRecipients);
  const [recipients, setRecipients] = useState(initialRecipients);
  const [step, setStep] = useState(1); // 1: Recipients, 2: Templates
  const [searchTerm, setSearchTerm] = useState('');
  const [paramValues, setParamValues] = useState({}); // { 1: "value", 2: "value" }
  const [importStats, setImportStats] = useState(null);
  const fileInputRef = useRef(null);

  const [recipientSearchTerm, setRecipientSearchTerm] = useState('');

  useEffect(() => {
    fetchTemplates();
  }, []);

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const data = await file.arrayBuffer();
      const workbook = read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = utils.sheet_to_json(worksheet, { header: 1 });

      let importedCount = 0;
      const newRecipients = [];

      // Start from row 1 (index 0 might be header, checking content)
      // Assuming Column A is Phone, Column B is Name
      jsonData.forEach((row, index) => {
        if (index === 0 && typeof row[0] === 'string' && row[0].toLowerCase().includes('phone')) return; // Skip likely header

        let phone = row[0] ? String(row[0]).replace(/\D/g, '') : '';
        const name = row[1] ? String(row[1]) : 'Desconocido';

        if (phone.length >= 8) { // Basic validation
          // Check if already exists in availableContacts
          const exists = availableContacts.find(r => r.wa_id === phone);
          if (!exists) {
            newRecipients.push({
              id: `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`, // Temp ID
              wa_id: phone,
              name: name,
              isTemp: true
            });
            importedCount++;
          }
        }
      });

      if (importedCount > 0) {
        setAvailableContacts(prev => [...prev, ...newRecipients]);
        setRecipients(prev => [...prev, ...newRecipients]); // Auto-select imported
        setImportStats(`Se importaron ${importedCount} contactos nuevos.`);
        setTimeout(() => setImportStats(null), 5000);
      } else {
        alert('No se encontraron números válidos o ya estaban agregados.');
      }

    } catch (error) {
      console.error('Error parsing file:', error);
      alert('Error leyendo el archivo. Asegúrate de que sea un Excel o CSV válido.');
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // Filter recipients for display based on search
  const filteredRecipients = availableContacts.filter(c =>
    (c.name && c.name.toLowerCase().includes(recipientSearchTerm.toLowerCase())) ||
    (c.wa_id && c.wa_id.includes(recipientSearchTerm))
  );

  // Line 22: const filteredRecipients = initialRecipients.filter(...)
  // So 'initialRecipients' was the LIST OF OPTIONS. And 'recipients' was... wait.
  // line 27: filteredRecipients.every(r => recipients.find(s => s.id === r.id));
  // It seems 'recipients' is the SELECTED list.
  // BUT line 10 initializes it with 'initialRecipients'. So initially ALL are selected?
  // If 'initialRecipients' passed from Sidebar are the *selected* conversations (e.g. from a filtered view?), then yes.

  // If I want to ADD imported contacts, I should add them to BOTH the "Available Options" and "Selected".
  // But the UI only has "recipients" state which seems to act as... wait.
  // If I change 'filteredRecipients' to filter from 'recipients', then I lose the "unselected" ones?
  // No, let's look at toggle:
  // toggleRecipient adds/removes from 'recipients'.
  // Where is the list of ALL POSSIBLE recipients stored?
  // Original: 'filteredRecipients' comes from 'initialRecipients'.
  // 'initialRecipients' is a prop. I cannot mutate it.

  // I need a local state for 'allContacts' which initializes with 'initialRecipients'.
  // Then data import adds to 'allContacts'.
  // And also adds to 'recipients' (selected) by default? Yes for better UX.

  // So I will introduce 'allContacts' state.

  const areAllVisibleSelected = filteredRecipients.length > 0 && filteredRecipients.every(r => recipients.find(s => s.id === r.id));

  const toggleSelectAll = () => {
    if (areAllVisibleSelected) {
      // Deselect all visible
      const visibleIds = filteredRecipients.map(r => r.id);
      setRecipients(prev => prev.filter(r => !visibleIds.includes(r.id)));
    } else {
      // Select all visible (add missing ones)
      const visibleIds = filteredRecipients.map(r => r.id);
      setRecipients(prev => {
        const newRecipients = [...prev];
        filteredRecipients.forEach(r => {
          if (!newRecipients.find(existing => existing.id === r.id)) {
            newRecipients.push(r);
          }
        });
        return newRecipients;
      });
    }
  };

  const fetchTemplates = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/templates`);
      setTemplates(res.data.data || []);
      setLoading(false);
    } catch (error) {
      console.error('Error cargando plantillas:', error);
      setLoading(false);
    }
  };

  const goToConfirmation = () => {
    if (selectedTemplate && recipients.length > 0) {
      setStep(3);
    }
  };

  const [selectedChannel, setSelectedChannel] = useState(''); // Default empty = use conversation default
  const [forceResend, setForceResend] = useState(false); // Bypass anti-duplicate protection

  const channels = [
    { id: '922986590894796',  name: '🏢 Comercial' },
    { id: '999551239899325',  name: '👔 Administrativo' },
    { id: '916595048209652',  name: '🌎 R. Internacionales' },
    { id: '1020657397786989', name: '📈 Ventas' },
    { id: '983548531506198',  name: '⭐ Regulares' },
    { id: '1308381959018456', name: '🇵🇪 Peru' },
    { id: '1000790306449643', name: 'ℹ️ Info 2' },
    { id: '1233806963139486', name: '🎓 Académico' },
    { id: '1232012809998778', name: '📦 Logística' },
    { id: '1348031501732308', name: '🤝 ONG' },
  ];

  /* ... */

  const handleFinalConfirm = () => {
    // Construct components array for Meta
    const components = [];
    const bodyParams = Object.keys(paramValues).sort().map(key => ({
      type: 'text',
      text: paramValues[key]
    }));

    if (bodyParams.length > 0) {
      components.push({
        type: 'body',
        parameters: bodyParams
      });
    }

    onSelect(selectedTemplate, recipients, components, selectedChannel, forceResend);
  };

  const toggleRecipient = (conv) => {
    setRecipients(prev => {
      const exists = prev.find(r => r.id === conv.id);
      if (exists) {
        return prev.filter(r => r.id !== conv.id);
      } else {
        return [...prev, conv];
      }
    });
  };

  // Filtered templates
  const filteredTemplates = templates.filter(t => t.name.toLowerCase().includes(searchTerm.toLowerCase()));
  const isDark = theme === 'dark';

  return (
    <div className={`modal-overlay broadcast-modal ${isDark ? 'broadcast-modal--dark' : ''}`} onClick={onClose}>
      <div className="modal-content broadcast-modal__content" onClick={e => e.stopPropagation()} style={{ maxWidth: '600px', width: '90%' }}>
        <div className="modal-header" style={{ backgroundColor: '#00a884', color: 'white', borderTopLeftRadius: '8px', borderTopRightRadius: '8px', padding: '16px' }}>
          <h3 style={{ margin: 0 }}>Nueva Difusión</h3>
          <button className="close-button" onClick={onClose} style={{ color: 'white', opacity: 0.8 }}>&times;</button>
        </div>

        <div className="modal-body" style={{ padding: '0', display: 'flex', flexDirection: 'column', height: '500px' }}>

          {/* Stepper Tabs */}
          <div className="stepper broadcast-stepper" style={{ display: 'flex', borderBottom: '1px solid #e9edef' }}>
            <button
              className={`step-btn ${step === 1 ? 'active' : ''}`}
              onClick={() => setStep(1)}
            >
              1. Destinatarios ({recipients.length})
            </button>
            <button
              className={`step-btn ${step === 2 ? 'active' : ''}`}
              onClick={() => setStep(2)}
              disabled={recipients.length === 0}
            >
              2. Plantilla
            </button>
          </div>

          <div className="step-content broadcast-step-content" style={{ flex: 1, overflowY: 'auto', padding: '16px' }}>
            {step === 1 && (
              <div className="recipients-section">
                <div className="broadcast-toolbar">
                  <div className="broadcast-toolbar-actions">
                    <input
                      type="file"
                      accept=".xlsx, .xls, .csv"
                      ref={fileInputRef}
                      style={{ display: 'none' }}
                      onChange={handleFileUpload}
                    />
                    <button
                      type="button"
                      className="broadcast-btn broadcast-btn-file"
                      onClick={() => fileInputRef.current.click()}
                      title="Subir Excel o CSV"
                    >
                      📂 Importar archivo
                    </button>
                  </div>
                  <input
                    type="text"
                    className="broadcast-search-input"
                    placeholder="🔍 Buscar en la lista..."
                    value={recipientSearchTerm}
                    onChange={e => setRecipientSearchTerm(e.target.value)}
                    style={{ flex: 1, minWidth: 120, padding: '8px 12px', borderRadius: '8px', border: '1px solid #e9edef' }}
                  />
                </div>

                {importStats && (
                  <div className="broadcast-import-stats">
                    {importStats}
                  </div>
                )}

                <div className="broadcast-count-row">
                  <span>Mostrando {filteredRecipients.length} de {availableContacts.length}</span>
                  <button type="button" className="broadcast-select-all-btn" onClick={toggleSelectAll}>
                    {areAllVisibleSelected ? 'Desmarcar todos' : 'Marcar todos'}
                  </button>
                </div>

                <div className="recipients-list">
                  {filteredRecipients.length === 0 ? (
                    <p className="broadcast-empty-msg">No se encontraron contactos.</p>
                  ) : (
                    filteredRecipients.map(conv => (
                      <div key={conv.id} className="recipient-item" onClick={() => toggleRecipient(conv)}>
                        <input
                          type="checkbox"
                          checked={!!recipients.find(r => r.id === conv.id)}
                          onChange={() => { }} // handled by div click
                          style={{ marginRight: '10px', accentColor: '#00a884', width: '18px', height: '18px' }}
                        />
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: '500' }}>{conv.name || 'Sin nombre'}</div>
                          <div style={{ fontSize: '12px', color: '#888' }}>{conv.wa_id}</div>
                        </div>
                        <span className="broadcast-recipient-chip">
                          {(() => {
                            const id = conv.channel_id || '';
                            if (id === '922986590894796') return 'Comercial';
                            if (id === '999551239899325') return 'Admin';
                            if (id === '916595048209652') return 'RRIO';
                            if (id === '1020657397786989') return 'Ventas';
                            if (id === '983548531506198') return 'Regulares';
                            if (id === '1308381959018456') return 'Peru';
                            if (id === '1000790306449643') return 'Info 2';
                            if (id === '1233806963139486') return '🎓 Académico';
                            if (id === '1232012809998778') return 'Logística';
                            if (id === '1348031501732308') return 'ONG';
                            return 'WhatsApp';
                          })()}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {step === 2 && (
              <div className="templates-section">
                <input
                  type="text"
                  className="broadcast-search-input"
                  placeholder="🔍 Buscar plantilla..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 12px',
                    borderRadius: '8px',
                    border: '1px solid #e9edef',
                    marginBottom: '10px'
                  }}
                />

                {loading ? (
                  <div className="loading-state">Cargando plantillas de WhatsApp...</div>
                ) : (
                  <div className="templates-list">
                    {filteredTemplates.length === 0 ? (
                      <div className="broadcast-empty-msg" style={{ textAlign: 'center', padding: '20px' }}>
                        <p>No se encontraron plantillas.</p>
                        <p className="broadcast-empty-note" style={{ fontSize: '12px', marginTop: '10px' }}>
                          Nota: Asegúrate de tener <code>META_WABA_ID</code> configurado en tu .env si tus plantillas no cargan.
                        </p>
                      </div>
                    ) : (
                      filteredTemplates.map(template => (
                        <div
                          key={template.id}
                          className={`template-item ${selectedTemplate?.id === template.id ? 'selected' : ''}`}
                          onClick={() => setSelectedTemplate(template)}
                        >
                          <div className="template-name">{template.name}</div>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                            <span className="template-category">{template.category}</span>
                            <span className="template-language">{template.language}</span>
                          </div>
                          <div className="template-preview">
                            {template.components.find(c => c.type === 'BODY')?.text}
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            )}
            {step === 3 && (
              <div className="confirmation-section broadcast-confirmation" style={{ padding: '20px', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                <div style={{ fontSize: '48px', marginBottom: '20px' }}>🚀</div>
                <h3 className="broadcast-confirm-title">¿Listo para enviar?</h3>
                <p className="broadcast-confirm-desc">
                  Estás a punto de enviar una difusión masiva.
                </p>

                <div className="broadcast-confirm-box">
                  <div className="broadcast-confirm-row">
                    <span className="broadcast-confirm-label">Destinatarios:</span>
                    <span className="broadcast-confirm-value broadcast-confirm-value--green">{recipients.length} contactos</span>
                  </div>
                  <div className="broadcast-confirm-row">
                    <span className="broadcast-confirm-label">Plantilla:</span>
                    <span className="broadcast-confirm-value">{selectedTemplate?.name}</span>
                  </div>

                  {/* Channel Selector */}
                  <div className="broadcast-confirm-row broadcast-confirm-row--align">
                    <span className="broadcast-confirm-label">Enviar desde:</span>
                    <select
                      className="broadcast-confirm-select"
                      value={selectedChannel}
                      onChange={(e) => setSelectedChannel(e.target.value)}
                    >
                      <option value="">Automático (Según contacto)</option>
                      {channels.map(ch => (
                        <option key={ch.id} value={ch.id}>{ch.name}</option>
                      ))}
                    </select>
                  </div>

                  {/* Force Resend Checkbox */}
                  <div className="broadcast-confirm-row broadcast-confirm-row--align" style={{ marginTop: '8px' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', userSelect: 'none', width: '100%' }}>
                      <input
                        type="checkbox"
                        checked={forceResend}
                        onChange={e => setForceResend(e.target.checked)}
                        style={{ width: '16px', height: '16px', accentColor: '#f59e0b', cursor: 'pointer', flexShrink: 0 }}
                      />
                      <span style={{ fontSize: '13px', color: isDark ? '#fbbf24' : '#92400e', fontWeight: 500 }}>
                        ⚡ Forzar reenvío (ignorar filtro anti-duplicado)
                      </span>
                    </label>
                  </div>

                  <div className="broadcast-confirm-footer">
                    <span className="broadcast-confirm-lang">Idioma: {selectedTemplate?.language || 'es'}</span>
                  </div>
                </div>

                {/* Variable Inputs */}
                {(() => {
                  const bodyComponent = selectedTemplate?.components?.find(c => c.type === 'BODY');
                  if (!bodyComponent || !bodyComponent.text) return null;

                  const matches = [...bodyComponent.text.matchAll(/{{(\d+)}}/g)];
                  if (matches.length === 0) return null;

                  const uniqueParams = [...new Set(matches.map(m => m[1]))].sort((a, b) => a - b);

                  return (
                    <div className="broadcast-vars-wrap">
                      <h4 className="broadcast-vars-title">Variables:</h4>
                      {uniqueParams.map(paramNum => (
                        <div key={paramNum} className="broadcast-var-item">
                          <label className="broadcast-var-label">Variable {paramNum}</label>
                          <input
                            type="text"
                            className="broadcast-var-input"
                            placeholder={`Valor para {{${paramNum}}}`}
                            value={paramValues[paramNum] || ''}
                            onChange={(e) => setParamValues(prev => ({ ...prev, [paramNum]: e.target.value }))}
                          />
                        </div>
                      ))}
                    </div>
                  );
                })()}

                <div className="broadcast-confirm-actions">
                  <button type="button" className="broadcast-back-btn" onClick={() => setStep(2)} disabled={isBroadcasting}>Atrás</button>
                  <button
                    type="button"
                    className="broadcast-send-btn"
                    onClick={isBroadcasting ? undefined : handleFinalConfirm}
                    disabled={isBroadcasting}
                    style={{ opacity: isBroadcasting ? 0.7 : 1, cursor: isBroadcasting ? 'not-allowed' : 'pointer' }}
                  >
                    {isBroadcasting ? '⏳ Enviando...' : 'Confirmar y Enviar'}
                  </button>
                  {isBroadcasting && onCancelBroadcast && (
                    <button
                      type="button"
                      onClick={onCancelBroadcast}
                      style={{ padding: '10px 20px', borderRadius: '24px', border: '2px solid #ef4444', background: 'transparent', color: '#ef4444', fontSize: '14px', fontWeight: 600, cursor: 'pointer' }}
                    >
                      ✕ Cancelar
                    </button>
                  )}
                </div>
                {isBroadcasting && broadcastProgress && (
                  <div style={{ width: '100%', maxWidth: '400px', marginTop: '16px' }}>
                    <div style={{ fontSize: '13px', color: isDark ? '#aebac1' : '#54656f', marginBottom: '6px', textAlign: 'center' }}>
                      Enviando mensaje único... por favor espera.
                    </div>
                    <div style={{ height: '6px', background: isDark ? '#2a3942' : '#e9edef', borderRadius: '10px', overflow: 'hidden' }}>
                      <div style={{
                        height: '100%',
                        width: '60%',
                        background: '#00a884',
                        borderRadius: '10px',
                        animation: 'broadcast-pulse 1.5s ease-in-out infinite'
                      }} />
                    </div>
                    <style>{`
                      @keyframes broadcast-pulse {
                        0% { width: 10%; }
                        50% { width: 80%; }
                        100% { width: 10%; }
                      }
                    `}</style>
                  </div>
                )}
              </div>
            )}
          </div>

        </div>

        <div className="modal-footer broadcast-modal-footer" style={{ borderTop: '1px solid #e9edef', padding: '16px', display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          {step === 3 ? (
            <div style={{ flex: 1 }}></div> // Footer empty or hidden for step 3
          ) : (
            <>
              <button className="cancel-btn" onClick={onClose}>Cancelar</button>
              {step === 1 ? (
                <button
                  className="confirm-btn"
                  disabled={recipients.length === 0}
                  onClick={() => setStep(2)}
                >
                  Siguiente ({recipients.length})
                </button>
              ) : (
                <button
                  className="confirm-btn"
                  disabled={!selectedTemplate || recipients.length === 0}
                  onClick={goToConfirmation}
                >
                  Siguiente
                </button>
              )}
            </>
          )}
        </div>
      </div>

      <style>{`
        .broadcast-modal.broadcast-modal--dark.modal-overlay { background-color: rgba(0, 0, 0, 0.75); }
        .broadcast-modal--dark .modal-content.broadcast-modal__content { background-color: #111b21; }
        .broadcast-modal--dark .broadcast-stepper { border-bottom-color: #2a3942; }
        .broadcast-modal--dark .step-btn { color: #8696a0; }
        .broadcast-modal--dark .step-btn.active { color: #00a884; background-color: rgba(0, 168, 132, 0.15); }
        .broadcast-modal--dark .step-btn:disabled { color: #667781; }
        .broadcast-modal--dark .broadcast-step-content { background-color: #111b21; color: #e9edef; }
        .broadcast-modal--dark .recipient-item { border-bottom-color: #2a3942; color: #e9edef; }
        .broadcast-modal--dark .recipient-item:hover { background-color: #2a3942; }
        .broadcast-modal--dark .recipient-item div { color: inherit; }
        .broadcast-modal--dark .recipient-item [style*="color: #888"] { color: #8696a0 !important; }
        .broadcast-modal--dark .template-item { border-color: #2a3942; color: #e9edef; }
        .broadcast-modal--dark .template-item:hover { background-color: #2a3942; }
        .broadcast-modal--dark .template-item.selected { border-color: #00a884; background-color: rgba(0, 168, 132, 0.15); }
        .broadcast-modal--dark .template-name { color: #e9edef; }
        .broadcast-modal--dark .template-category, .broadcast-modal--dark .template-language { background-color: #2a3942; color: #aebac1; }
        .broadcast-modal--dark .template-preview { background-color: #2a3942; color: #aebac1; }
        .broadcast-modal--dark .loading-state { color: #8696a0; }
        .broadcast-modal--dark .cancel-btn { color: #aebac1; }
        .broadcast-modal--dark .cancel-btn:hover { color: #e9edef; background-color: #2a3942; }
        .broadcast-modal--dark .confirm-btn:not(:disabled) { background-color: #00a884; color: #fff; }
        .broadcast-modal--dark .confirm-btn:disabled { background-color: #2a3942; color: #667781; }
        .broadcast-modal--dark .broadcast-import-btn { background-color: #2a3942 !important; border-color: #3b4a54 !important; color: #e9edef !important; }
        .broadcast-modal--dark .broadcast-import-btn:hover { background-color: #3b4a54 !important; }
        .broadcast-modal--dark .broadcast-search-input { background-color: #2a3942 !important; border-color: #2a3942 !important; color: #e9edef !important; }
        .broadcast-modal--dark .broadcast-search-input::placeholder { color: #8696a0; }
        .broadcast-modal--dark .broadcast-count-row { color: #8696a0 !important; }
        .broadcast-modal--dark .broadcast-select-all-btn { color: #00a884 !important; background: none !important; border: none !important; cursor: pointer; font-weight: 600; }
        .broadcast-modal--dark .broadcast-recipient-chip { background-color: #3b4a54 !important; color: #aebac1 !important; }
        .broadcast-modal--dark .broadcast-import-stats { background-color: rgba(0, 168, 132, 0.2) !important; color: #00a884 !important; }
        .broadcast-modal--dark .broadcast-empty-msg { color: #8696a0 !important; }
        .broadcast-modal--dark .broadcast-empty-note { color: #8696a0 !important; }
        .broadcast-modal--dark .broadcast-modal-footer { border-top-color: #2a3942 !important; }
        .broadcast-modal--dark .broadcast-confirm-title { color: #8696a0 !important; }
        .broadcast-modal--dark .broadcast-confirm-desc { color: #8696a0 !important; }
        .broadcast-modal--dark .broadcast-confirm-box { background-color: #2a3942 !important; border-radius: 12px; padding: 20px; width: 100%; max-width: 400px; text-align: left; margin-bottom: 30px; }
        .broadcast-modal--dark .broadcast-confirm-label { color: #aebac1 !important; }
        .broadcast-modal--dark .broadcast-confirm-value { color: #e9edef !important; }
        .broadcast-modal--dark .broadcast-confirm-value--green { color: #00a884 !important; }
        .broadcast-modal--dark .broadcast-confirm-row { display: flex; justify-content: space-between; margin-bottom: 10px; }
        .broadcast-modal--dark .broadcast-confirm-row--align { align-items: center; }
        .broadcast-modal--dark .broadcast-confirm-select { padding: 4px 8px; border-radius: 4px; border: 1px solid #3b4a54; background-color: #111b21; color: #e9edef; font-size: 13px; max-width: 180px; }
        .broadcast-modal--dark .broadcast-confirm-footer { border-top: 1px solid #3b4a54; margin-top: 10px; padding-top: 10px; }
        .broadcast-modal--dark .broadcast-confirm-lang { font-size: 12px; color: #8696a0; }
        .broadcast-modal--dark .broadcast-vars-title { color: #8696a0 !important; }
        .broadcast-modal--dark .broadcast-var-label { color: #8696a0 !important; }
        .broadcast-modal--dark .broadcast-var-input { background-color: #2a3942 !important; border-color: #3b4a54 !important; color: #e9edef !important; }
        .broadcast-modal--dark .broadcast-back-btn { background-color: #2a3942 !important; border-color: #3b4a54 !important; color: #e9edef !important; }
        .broadcast-modal--dark .broadcast-back-btn:hover { background-color: #3b4a54 !important; }
        .broadcast-modal--dark .broadcast-send-btn { background-color: #00a884 !important; color: #fff !important; }
        .broadcast-import-btn { padding: 8px 12px; background-color: #f0f2f5; border: 1px solid #d1d7db; border-radius: 8px; cursor: pointer; font-size: 14px; }
        .broadcast-count-row { display: flex; justify-content: space-between; align-items: center; margin-bottom: 10px; font-size: 14px; color: #54656f; }
        .broadcast-select-all-btn { color: #00a884; font-weight: 600; }
        .broadcast-recipient-chip { font-size: 11px; padding: 2px 6px; border-radius: 4px; display: inline-block; background-color: #f0f2f5; color: #54656f; }
        .broadcast-import-stats { margin-bottom: 10px; padding: 8px; border-radius: 6px; font-size: 13px; background-color: #d1fae5; color: #065f46; }
        .broadcast-empty-msg { text-align: center; padding: 20px; color: #888; margin: 0; }
        .broadcast-toolbar { display: flex; align-items: center; gap: 12px; margin-bottom: 14px; flex-wrap: wrap; }
        .broadcast-toolbar-actions { display: flex; gap: 8px; flex-shrink: 0; }
        .broadcast-btn { padding: 8px 14px; border-radius: 8px; border: 1px solid #d1d7db; background: #fff; color: #54656f; font-size: 14px; font-weight: 500; cursor: pointer; transition: all 0.2s; }
        .broadcast-btn:hover { background: #f5f6f6; border-color: #00a884; color: #00a884; }
        .broadcast-btn-sheet.active { background: #e8f5e9; border-color: #00a884; color: #027f68; }
        .broadcast-sheet-card { background: #f8f9fa; border: 1px solid #e9edef; border-radius: 12px; padding: 16px; margin-bottom: 14px; }
        .broadcast-sheet-card-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
        .broadcast-sheet-card-title { margin: 0; font-size: 15px; font-weight: 600; color: #111b21; }
        .broadcast-sheet-card-close { background: none; border: none; font-size: 20px; color: #8696a0; cursor: pointer; line-height: 1; padding: 0 4px; }
        .broadcast-sheet-card-close:hover { color: #111b21; }
        .broadcast-sheet-card-hint { margin: 0 0 12px; font-size: 13px; color: #54656f; line-height: 1.45; }
        .broadcast-sheet-card-form { display: flex; gap: 10px; flex-wrap: wrap; }
        .broadcast-sheet-input { flex: 1; min-width: 200px; padding: 10px 12px; border: 1px solid #e9edef; border-radius: 8px; font-size: 14px; }
        .broadcast-sheet-input:focus { outline: none; border-color: #00a884; box-shadow: 0 0 0 2px rgba(0,168,132,0.15); }
        .broadcast-sheet-submit { padding: 10px 18px; border-radius: 8px; border: none; background: #00a884; color: #fff; font-size: 14px; font-weight: 600; cursor: pointer; }
        .broadcast-sheet-submit:hover:not(:disabled) { background: #029273; }
        .broadcast-sheet-submit:disabled { background: #a0b0a8; cursor: not-allowed; }
        .broadcast-sheet-error { margin-top: 10px; padding: 10px 12px; background: #fef2f2; color: #b91c1c; border-radius: 8px; font-size: 13px; }
        .broadcast-modal--dark .broadcast-btn { background: #2a3942; border-color: #3b4a54; color: #e9edef; }
        .broadcast-modal--dark .broadcast-btn:hover { background: #3b4a54; border-color: #00a884; color: #00a884; }
        .broadcast-modal--dark .broadcast-btn-sheet.active { background: rgba(0,168,132,0.2); border-color: #00a884; color: #00a884; }
        .broadcast-modal--dark .broadcast-sheet-card { background: #202c33; border-color: #2a3942; }
        .broadcast-modal--dark .broadcast-sheet-card-title { color: #e9edef; }
        .broadcast-modal--dark .broadcast-sheet-card-close { color: #8696a0; }
        .broadcast-modal--dark .broadcast-sheet-card-close:hover { color: #e9edef; }
        .broadcast-modal--dark .broadcast-sheet-card-hint { color: #aebac1; }
        .broadcast-modal--dark .broadcast-sheet-input { background: #111b21; border-color: #2a3942; color: #e9edef; }
        .broadcast-modal--dark .broadcast-sheet-input:focus { border-color: #00a884; }
        .broadcast-modal--dark .broadcast-sheet-submit { background: #00a884; }
        .broadcast-modal--dark .broadcast-sheet-submit:disabled { background: #3b4a54; color: #667781; }
        .broadcast-modal--dark .broadcast-sheet-error { background: rgba(185,28,28,0.25); color: #fca5a5; }
        .broadcast-sheet-preview-meta { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; flex-wrap: wrap; gap: 8px; }
        .broadcast-sheet-preview-count { font-size: 13px; color: #54656f; font-weight: 500; }
        .broadcast-sheet-back-link { background: none; border: none; color: #00a884; font-size: 13px; cursor: pointer; text-decoration: underline; }
        .broadcast-sheet-back-link:hover { color: #027f68; }
        .broadcast-sheet-columns { display: flex; gap: 20px; margin-bottom: 12px; flex-wrap: wrap; }
        .broadcast-sheet-col-label { display: flex; align-items: center; gap: 8px; font-size: 14px; color: #111b21; }
        .broadcast-sheet-col-label span { white-space: nowrap; }
        .broadcast-sheet-select { padding: 6px 10px; border: 1px solid #e9edef; border-radius: 8px; font-size: 14px; min-width: 120px; background: #fff; }
        .broadcast-sheet-table-wrap { overflow-x: auto; margin-bottom: 14px; border: 1px solid #e9edef; border-radius: 10px; background: #fff; }
        .broadcast-sheet-table { width: 100%; border-collapse: collapse; font-size: 13px; }
        .broadcast-sheet-table th, .broadcast-sheet-table td { padding: 8px 10px; text-align: left; border-bottom: 1px solid #f0f2f5; }
        .broadcast-sheet-table th { background: #f5f6f6; font-weight: 600; color: #54656f; }
        .broadcast-sheet-table td.col-phone { background: #e8f5e9; }
        .broadcast-sheet-table td.col-name { background: #e3f2fd; }
        .broadcast-sheet-table th.col-phone { background: #c8e6c9; color: #1b5e20; }
        .broadcast-sheet-table th.col-name { background: #bbdefb; color: #0d47a1; }
        .broadcast-sheet-table-more { margin: 6px 10px 0; font-size: 12px; color: #8696a0; }
        .broadcast-sheet-card-actions { margin-top: 4px; }
        .broadcast-sheet-submit-primary { width: 100%; }
        .broadcast-modal--dark .broadcast-sheet-preview-count { color: #aebac1; }
        .broadcast-modal--dark .broadcast-sheet-back-link { color: #00a884; }
        .broadcast-modal--dark .broadcast-sheet-col-label { color: #e9edef; }
        .broadcast-modal--dark .broadcast-sheet-select { background: #111b21; border-color: #2a3942; color: #e9edef; }
        .broadcast-modal--dark .broadcast-sheet-table-wrap { border-color: #2a3942; background: #202c33; }
        .broadcast-modal--dark .broadcast-sheet-table th { background: #2a3942; color: #aebac1; }
        .broadcast-modal--dark .broadcast-sheet-table td { border-bottom-color: #2a3942; color: #e9edef; }
        .broadcast-modal--dark .broadcast-sheet-table td.col-phone { background: rgba(0,168,132,0.2); }
        .broadcast-modal--dark .broadcast-sheet-table td.col-name { background: rgba(33,150,243,0.2); }
        .broadcast-modal--dark .broadcast-sheet-table th.col-phone { background: rgba(0,168,132,0.35); color: #00a884; }
        .broadcast-modal--dark .broadcast-sheet-table th.col-name { background: rgba(33,150,243,0.35); color: #64b5f6; }
        .broadcast-modal--dark .broadcast-sheet-table-more { color: #8696a0; }
        .step-btn {
            flex: 1;
            padding: 12px;
            background: none;
            border: none;
            border-bottom: 3px solid transparent;
            font-weight: 600;
            color: #54656f;
            cursor: pointer;
            transition: all 0.2s;
        }
        .step-btn.active {
            border-bottom-color: #00a884;
            color: #00a884;
            background-color: #f0fdf4;
        }
        .step-btn:disabled {
            color: #ccc;
            cursor: not-allowed;
        }

        .recipient-item {
            display: flex;
            align-items: center;
            padding: 10px;
            border-bottom: 1px solid #f0f2f5;
            cursor: pointer;
        }
        .recipient-item:hover {
            background-color: #f5f6f6;
        }

        .loading-state {
          text-align: center;
          padding: 20px;
          color: #666;
        }
        .templates-list {
          display: flex;
          flex-direction: column;
          gap: 10px;
        }
        .template-item {
          border: 1px solid #e9edef;
          padding: 12px;
          border-radius: 8px;
          cursor: pointer;
          transition: all 0.2s;
        }
        .template-item:hover {
          background-color: #f5f6f6;
        }
        .template-item.selected {
          border-color: #00a884;
          background-color: #f0fdf4;
          box-shadow: 0 0 0 1px #00a884;
        }
        .template-name {
          font-weight: 600;
          color: #111b21;
          margin-bottom: 4px;
        }
        .template-category, .template-language {
          display: inline-block;
          font-size: 11px;
          padding: 2px 6px;
          background-color: #e9edef;
          color: #54656f;
          border-radius: 4px;
        }
        .template-preview {
          font-size: 13px;
          color: #54656f;
          white-space: pre-wrap;
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          background-color: #f0f2f5;
          padding: 8px;
          border-radius: 6px;
          margin-top: 6px;
        }
        .cancel-btn {
          background: none;
          border: none;
          color: #666;
          padding: 10px 20px;
          cursor: pointer;
          font-size: 14px;
        }
        .confirm-btn {
          background-color: #00a884;
          color: white;
          border: none;
          padding: 10px 24px;
          border-radius: 24px;
          font-weight: 600;
          cursor: pointer;
        }
        .confirm-btn:disabled {
          background-color: #e9edef;
          color: #aebac1;
          cursor: not-allowed;
        }
        .broadcast-confirm-title { color: #54656f; margin-bottom: 10px; }
        .broadcast-confirm-desc { color: #888; margin-bottom: 30px; }
        .broadcast-confirm-box { background-color: #f0f2f5; padding: 20px; border-radius: 12px; width: 100%; max-width: 400px; text-align: left; margin-bottom: 30px; }
        .broadcast-confirm-row { display: flex; justify-content: space-between; margin-bottom: 10px; }
        .broadcast-confirm-row--align { align-items: center; }
        .broadcast-confirm-label { color: #54656f; font-weight: bold; }
        .broadcast-confirm-value { color: #111b21; }
        .broadcast-confirm-value--green { color: #00a884; font-weight: bold; }
        .broadcast-confirm-select { padding: 4px 8px; border-radius: 4px; border: 1px solid #d1d7db; background-color: white; font-size: 13px; max-width: 180px; }
        .broadcast-confirm-footer { border-top: 1px solid #e9edef; margin-top: 10px; padding-top: 10px; }
        .broadcast-confirm-lang { font-size: 12px; color: #888; }
        .broadcast-vars-wrap { width: 100%; max-width: 400px; margin-bottom: 20px; }
        .broadcast-vars-title { color: #54656f; margin-bottom: 10px; text-align: left; }
        .broadcast-var-item { margin-bottom: 10px; text-align: left; }
        .broadcast-var-label { display: block; font-size: 12px; color: #888; margin-bottom: 4px; }
        .broadcast-var-input { width: 100%; padding: 8px 12px; border-radius: 8px; border: 1px solid #e9edef; }
        .broadcast-confirm-actions { display: flex; gap: 15px; }
        .broadcast-back-btn { padding: 10px 24px; border-radius: 24px; border: 1px solid #e9edef; background-color: white; color: #54656f; font-size: 14px; font-weight: 600; cursor: pointer; }
        .broadcast-send-btn { padding: 10px 24px; border-radius: 24px; border: none; background-color: #00a884; color: white; font-size: 14px; font-weight: 600; cursor: pointer; box-shadow: 0 4px 6px rgba(0,168,132,0.2); }
      `}</style>
    </div>
  );
}

export default TemplateSelector;
